import { FC, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom'
import Kulturio from '@ekultur/kulturio-bridge';
import styled from 'styled-components';
import { AUTOPLAY_IMAGE_TIMER, MEDIA_PREFIX } from '../Settings'
import { fetchDocument, fetchMedia } from '../services/Api';
import { getTranslation } from '../common/Translation';
import { useTranslation } from 'react-i18next';
import ErrorMessageView from '../components/ErrorMessage';
import PageTransition from '../components/PageTransition';
import Header from '../framework/Header';
import { AppContext } from '../contexts/AppContext';
import useFetchDocument from '../hooks/useFetchDocument';
import PdfViewer from '../components/PdfViewer';
import Model3dPopup from '../components/LightboxModal_3D';
import { usePlausiblePageview } from '../hooks/usePlausibleTracker';
import PlayIconSvg from "../assets/icon_play.svg?url";
import { u } from '../common/Units';
import { getMenuDisplayMode } from '../common/MenuDisplay';
import useAutoplayRecovery from '../hooks/useAutoplayRecovery';

const Image = styled.img<{ $objectFit?: string, $height?: string|number }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: ${({ $height = "100%" }) => $height};
  object-fit: ${({ $objectFit = "contain" }) => $objectFit};
  opacity: 1;
  transition: opacity 0.5s;
  background: #000;

  &.loading {
    opacity: 0;
  }
`;

const Video = styled.video<{ $objectFit?: string, $height?: string|number }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: ${({ $height = "100%" }) => $height};
  object-fit: ${({ $objectFit = "contain" }) => $objectFit};
  opacity: 1;
  transition: opacity 0.5s;
  background: transparent;
  filter: brightness(0.99);

  &.loading {
    opacity: 0;
  }

  &::cue {
    color: #fff;
    line-height: 80px;
  }
`;

const Screenshot = styled.canvas<{ $objectFit?: string, $height?: string }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: ${({ $height = "100%" }) => $height};
  object-fit: ${({ $objectFit = "contain" }) => $objectFit};
  opacity: 1;
  background: transparent;
`;

const PlayContainer = styled.div`
  &:before {
    content: "";
    opacity: 0;
    transition: opacity: 2s;
    transition-delay: 1s;
  }

  &.paused:before {
    content: "";
    position: absolute;
    z-index: 10;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    border-radius: 40px;
    background-color: ${p => p.theme.accentColor};
    background-image: url("${PlayIconSvg}");
    background-position: 60% 50%;
    background-size: 30px;
    background-repeat: no-repeat;
    pointer-events: none;
    opacity: 1;
  }
`;

/*
 * Subtitles rendered by the app instead of the browser (track mode "hidden"). The browser lays native cues out
 * relative to the video frame, so they were cropped with object-fit: cover and could end up behind the bottom menu.
 */
const Subtitles = styled.div`
  position: absolute;
  left: 50%;
  /* Just above the bottom menu pills, or 5vh from the bottom without a bottom menu */
  bottom: max(5vh, calc(var(--menu-bottom-space, 0px) + 1vh));
  transform: translateX(-50%);
  z-index: 5;
  width: max-content;
  max-width: 90%;
  text-align: center;
  pointer-events: none;

  /*
   * TV-style subtitles: one box per line, with no gaps between lines.
   * The font box of Inter has ~0.24em empty space above the capitals but almost none below the descenders,
   * so the padding is uneven (0.06em top, 0.23em bottom) to make the visible space above and below look equal.
   * Line height = font box (1.21em) + padding (0.29em) = 1.5, so the line boxes meet.
   * Only the outer box, not spans from the cue markup.
   */
  & > span {
    display: inline;
    padding: 0.06em 0.5em 0.23em;
    font-size: ${u(36)};
    font-weight: 500;
    line-height: 1.5;
    letter-spacing: 0.01em;
    color: #fff;
    background-color: rgba(0, 0, 0, 0.75);
    border-radius: 0.2em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
    white-space: pre-line;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  /* WebVTT inline markup from getCueAsHTML(): <i>, <b>, <u> work as is. Default color classes: */
  & .white { color: #fff; }
  & .lime { color: #0f0; }
  & .cyan { color: #0ff; }
  & .red { color: #f00; }
  & .yellow { color: #ff0; }
  & .magenta { color: #f0f; }
  & .blue { color: #00f; }
  & .black { color: #000; }
  & .bg_white { background-color: #fff; }
  & .bg_lime { background-color: #0f0; }
  & .bg_cyan { background-color: #0ff; }
  & .bg_red { background-color: #f00; }
  & .bg_yellow { background-color: #ff0; }
  & .bg_magenta { background-color: #f0f; }
  & .bg_blue { background-color: #00f; }
  & .bg_black { background-color: #000; }
`;

const ProgressBarWrapper = styled.div`
  position: absolute;
  bottom: 0px;
  width: 100%;
  height: 10px;
  background-color: ${p => p.theme.backgroundColor};

  &::after {
    position: absolute;
    width: 100%;
    height: 100px;
    top: 50%;
    left: 0;
    content: "";
    transform: translateY(-50%);
  }
`

const ProgressBar = styled.div.attrs((p: any) => ({
  color: p.color || p.theme.accentColor
}))`
  width: 0%;
  height: 10px;
  background-color: ${p => p.color};
  transition: width 200ms;
`

const Iframe = styled.iframe`
  position: absolute; 
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: none;
`;

interface AutoPlayViewProps {
  previewData?: any,
  overrideTitle?: string
}

/**
 * Render autoplay view
 * @returns {JSX.Element} Component template
 */
const AutoPlayView: FC<AutoPlayViewProps> = ({previewData=null, overrideTitle}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screenshotRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<any>(null);
  const videoRef = useRef<any>(null);
  // Restarts the video if it pauses unexpectedly (e.g. when screen sharing to the kiosk ends)
  const bindVideo = useAutoplayRecovery(videoRef);
  const trackRef = useRef<HTMLTrackElement>(null);
  const playbuttonRef = useRef<any>(null);
  const timeout = useRef<ReturnType<typeof setInterval>>(undefined);
  const progressRef = useRef<HTMLDivElement>(null);
  const progressWrapperRef = useRef<HTMLDivElement>(null);
  const { deviceId, setMediaIsPlaying, currentLanguage, globalVolume, previewIsActive, setHideHeader, hideHeader, deviceData, deviceMenu } = useContext(AppContext);
  const [videoCropped, setVideoCropped] = useState<boolean>(false);
  const hasBottomMenu = ((hideHeader && !previewIsActive) ? null : getMenuDisplayMode(deviceData, deviceMenu)) === "bottom";
  // Subtitles are rendered by the app when the browser cannot place them correctly: with a bottom menu (they must stay
  // above it), or when object-fit: cover crops the video (the browser places cues on the full, partly hidden frame).
  // Otherwise the browser renders them as before.
  const customSubtitles = hasBottomMenu || videoCropped;
  const { documentId } = useParams<{documentId: string}>();
  const [mediaQueue, setMediaQueue] = useState<any>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentFile, setCurrentFile] = useState<any>({});
  const [useOriginalVideoFiles, setUseOriginalVideoFiles] = useState<boolean>(false);
  const [data, error] = useFetchDocument(documentId, previewData);
  const [closedCaptions, setClosedCaptions] = useState<any>(null);
  const [subtitleCues, setSubtitleCues] = useState<VTTCue[]>([]);
  const subtitleRef = useRef<HTMLSpanElement>(null);
  const [keyboardTriggerIsActive, setKeyboardTriggerIsActive] = useState<boolean>(false);
  const [contentHeight, setContentHeight] = useState<number|undefined>(undefined);
  const returnAfterPlay = !/device\/(\d+)(\/)?$/.test(window?.location?.hash);
  usePlausiblePageview({ deviceId: String(deviceId), documentId: documentId, documentType: 'autoplay', ownerId: data?.owner_id, ownerSlug: data?.owner_slug, language: currentLanguage}, { waitFor: ['ownerSlug'] });

  // Get height of content from Kulturio Desktop config
  useEffect(() => {
    const height = Kulturio.getConfig("interface.contentHeight");
    height && setContentHeight(height);
  }, []);

  /**
   * Capture thumbnail from video and provide is into the canvas element
   */
  const captureThumbnail = useCallback(() => {
    if (screenshotRef.current && videoRef.current) {
      const video = videoRef.current;
      
      // Only perform this if video has started
      if (video.currentTime > 0.1) {
        const canvas = screenshotRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = screenshotRef.current.getContext('2d');
        if (ctx) {
          ctx.imageSmoothingEnabled = video.imageSmoothingEnabled;
          ctx.imageSmoothingQuality = video.imageSmoothingQuality;
          ctx.globalAlpha = 1;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        }
      }
    }
  }, []);

  /**
   * Disable running subtitles
   */
  const removeAllSubtitles = useCallback(() => {
    if (!videoRef.current) { return }
    const textTracks = videoRef.current.textTracks;
    for (var i = 0; i < textTracks.length || 0; i++) {
      textTracks[i].mode = 'disabled';
    }
    setSubtitleCues([]);
  }, []);

  // Get captions
  useEffect(() => {
    if (!currentFile || !currentLanguage) { return }

    if (currentFile?.closedCaptions?.[currentLanguage || "no"]) {
      const encoder = new TextEncoder();
      const uint8Array = encoder.encode(currentFile?.closedCaptions?.[currentLanguage || "no"]);
      const vttBase64 = btoa(String.fromCharCode.apply(null, Array.from(uint8Array)));

      setClosedCaptions(`data:text/vtt;charset=utf-8;base64,${vttBase64}`);
    } else {
      setClosedCaptions(null);
    }
  }, [currentLanguage, currentFile]);

  // Detect if object-fit: cover crops the video frame
  useEffect(() => {
    const video = videoRef.current as HTMLVideoElement | null;
    if (!video) { setVideoCropped(false); return; }

    const update = () => {
      const { clientWidth: width, clientHeight: height, videoWidth, videoHeight } = video;
      if (!width || !height || !videoWidth || !videoHeight || getComputedStyle(video).objectFit !== "cover") {
        setVideoCropped(false);
        return;
      }
      const scale = Math.max(width / videoWidth, height / videoHeight);
      // Allow a couple of pixels for rounding
      setVideoCropped(videoWidth * scale > width + 2 || videoHeight * scale > height + 2);
    };

    update();
    video.addEventListener("loadedmetadata", update);
    window.addEventListener("resize", update);
    return () => {
      video.removeEventListener("loadedmetadata", update);
      window.removeEventListener("resize", update);
    };
  }, [currentFile]);

  // Show the active subtitle ourselves. The track is "hidden": the browser keeps track of the cues and
  // fires cuechange, but does not render them.
  useEffect(() => {
    const trackElement = trackRef.current;
    setSubtitleCues([]);
    if (!trackElement || !closedCaptions) { return; }

    const textTrack = trackElement.track;
    if (!customSubtitles) {
      // Native rendering, as before (keeps cue settings and STYLE blocks in the VTT file)
      if (textTrack.mode === "hidden") { textTrack.mode = "showing"; }
      return;
    }
    textTrack.mode = "hidden";

    const onCueChange = () => {
      if (textTrack.mode === "disabled") { setSubtitleCues([]); return; }
      setSubtitleCues(Array.from(textTrack.activeCues || []) as VTTCue[]);
    };

    textTrack.addEventListener("cuechange", onCueChange);
    // Show a cue that is already active (e.g. when the bottom menu appears mid-cue)
    onCueChange();
    return () => textTrack.removeEventListener("cuechange", onCueChange);
  }, [closedCaptions, customSubtitles]);

  // STYLE blocks from the VTT file, scoped to the app-rendered subtitles.
  // "::cue" styles the subtitle box, "::cue(selector)" styles markup inside it (e.g. <c.nor> → .nor).
  const subtitleStyles = useMemo(() => {
    const vtt: string | undefined = currentFile?.closedCaptions?.[currentLanguage || "no"];
    if (!customSubtitles || !vtt) { return ""; }
    const blocks = vtt.replace(/\r\n?/g, "\n").split(/\n{2,}/)
      .filter(block => /^STYLE\s*\n/.test(block))
      .map(block => block.replace(/^STYLE\s*\n/, ""));
    return blocks.join("\n")
      .replace(/::cue\(([^)]*)\)/g, ".kiosk-subtitles > span $1")
      .replace(/::cue\b/g, ".kiosk-subtitles > span");
  }, [currentFile, currentLanguage, customSubtitles]);

  // Render the active cues with their WebVTT inline markup (<i>, <b>, <u>, <c.class>, <v>, <ruby>).
  // getCueAsHTML() returns a DOM fragment built by the browser, so cue text is never parsed as raw HTML.
  useLayoutEffect(() => {
    const element = subtitleRef.current;
    if (!element) { return; }
    element.replaceChildren();
    subtitleCues.forEach((cue, i) => {
      if (i > 0) { element.appendChild(document.createElement("br")); }
      element.appendChild(cue.getCueAsHTML());
    });
  }, [subtitleCues]);

  // Play video on click. Fallback if browser is not supporting autoplay
  const playVideo = useCallback(() => {
    videoRef.current?.play();
  }, []);

  // Get presentation data from the API
  useEffect(() => {
    if (data?.content?.settings?.display?.useOriginalMedia === "true") {
      setUseOriginalVideoFiles(true);
    }
  }, [data, setUseOriginalVideoFiles]);

  // Create queue of media files, containing media source URL and media type
  useEffect(() => {
    let isSubscribed = true;
    if (!data?.content) { return; }

    (async() => {
      let mediaIds: number[] = [];

      // Get list of all media objects
      if (data?.content?.media) {
        for (const mediaItem of data?.content?.media) {
          mediaIds.push(mediaItem?.mediaId);
        }
      }

      // Abort if mediaIds has not changed
      if (mediaQueue.length === mediaIds.length && mediaIds.length > 0 && mediaQueue?.map(item => item?.id)?.[0] === mediaIds?.[0]) { return }

      // Get media objects from articles
      if (data?.content?.records?.list) {
        for (const articleItem of data?.content?.records?.list) {
          // Fetch all articles
          await fetchDocument(articleItem?.to_document_id).then(articleData => {
              // Loop through all blocks
              if (articleData?.content?.content?.blocks?.[currentLanguage || "no"]) {
                articleData?.content?.content?.blocks?.[currentLanguage || "no"]?.forEach((block) => {
                  if (block?.blockType === "media") {
                    // Loop through all media items in media blocks
                    block?.mediaItems?.forEach((mediaBlockItem: any) => {
                      mediaIds.push(mediaBlockItem?.mediaId);
                    });
                  }
                });
              }
          });
        }
      }

      // Fetch all media URLs and store in mediaQueue
      if (isSubscribed && mediaIds) {
        let mediaQueueCache: any[] = [];
        for (const mediaId of mediaIds) {
          await fetchMedia(mediaId, {includeOriginalUrl: useOriginalVideoFiles}).then(mediaMetadata => {
            mediaQueueCache.push({
              id: mediaId,
              src: MEDIA_PREFIX + (useOriginalVideoFiles ? mediaMetadata?.originalUrl || mediaMetadata?.src : mediaMetadata?.src),
              thumbnail: MEDIA_PREFIX + mediaMetadata?.thumbnail_src,
              mediaType: mediaMetadata?.media_type,
              closedCaptions: mediaMetadata?.closed_captions,
              volume: mediaMetadata?.volume
            });
          });
        }

        setMediaQueue(mediaQueueCache);
      }
    })();

    return () => { isSubscribed = false; }
    // eslint-disable-next-line
  }, [data, useOriginalVideoFiles, currentLanguage, mediaQueue.length]);

  // Get the current file
  useEffect(() => {
    if (currentFile?.src !== mediaQueue?.[currentIndex]?.src) {
      setCurrentFile(mediaQueue?.[currentIndex]);
    }
  }, [currentIndex, mediaQueue, currentFile])

  /**
   * Increment current media index
   */
  const incrementIndex = useCallback(() => {
    let mediaCount = Object.keys(mediaQueue)?.length;

    if (returnAfterPlay && currentIndex + 1 >= mediaCount) {
      if (window.history.length > 1) {
        setMediaIsPlaying(false);
        navigate(-1);
      } else {
        deviceId && navigate(`/device/${deviceId}`)
      }
    }

    if (mediaCount > 1) {
      imageRef?.current?.classList?.add("loading");
    }
    videoRef?.current?.classList?.add("loading");

    setTimeout(() => {
      if (currentIndex + 1 >= mediaCount) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    }, 500);
  }, [currentIndex, mediaQueue, returnAfterPlay, deviceId, navigate, setMediaIsPlaying]);

  /**
   * Display image and start image timer
  * @param e Event
   */
  const displayImage = useCallback((e: React.ChangeEvent<HTMLImageElement>) => {
    e?.target.classList.remove("loading");

    timeout.current && clearTimeout(timeout.current);

    timeout.current = setTimeout(() => {
      incrementIndex();
      clearTimeout(timeout.current);
    }, data?.content?.settings?.display?.delay || AUTOPLAY_IMAGE_TIMER);
  }, [incrementIndex, data?.content?.settings?.display?.delay]);

  /**
   * Display video when loaded
   * @param e Event
   */
  const displayVideo = useCallback((e: React.ChangeEvent<HTMLVideoElement>) => {
    e.target.classList.remove("loading");

    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, []);

  // Update video volume when global volume has changed
  useEffect(() => {
    const localVolume = (currentFile?.volume || 75) * 0.01;

    if (videoRef.current) {
      if (globalVolume !== undefined) {
        videoRef.current.volume = globalVolume * localVolume;
      } else {
        videoRef.current.volume = localVolume;
      }
    }
  }, [globalVolume, currentFile]);

  /**
   * Hide play button when video starts playing
   */
   const onPlaying = useCallback(() => {
    const localVolume = (currentFile?.volume || 75) * 0.01;
    setMediaIsPlaying(true);

    playbuttonRef.current.classList.remove("paused");
    if (videoRef.current) {
      if (globalVolume !== undefined) {
        videoRef.current.volume = globalVolume * localVolume;
      } else {
        videoRef.current.volume = localVolume;
      }
    }
  }, [globalVolume, currentFile, setMediaIsPlaying]);

  /**
   * Set currentTime based on click
   * @param e Event
   */
  const setProgressPosition = useCallback((e: any) => {
    if (videoRef.current && progressWrapperRef.current) {
      const clickedPositionPercentage = e.nativeEvent.offsetX / progressWrapperRef.current.clientWidth;
      const clickedPositionSeconds = videoRef.current.duration * clickedPositionPercentage;
      if (isNaN(clickedPositionSeconds)) { return }
      videoRef.current.currentTime = clickedPositionSeconds;
    }
  }, []);

  /**
   * Update progress bar when video is playing
   * @param e Event
   */
  const updateProgressBar = useCallback((e: any) => {
    if (progressRef.current) {
      const currentPosition = (e.target.currentTime / e.target.duration) * 100;
      progressRef.current.style.width = `${currentPosition}%`;
      setMediaIsPlaying(true);
    }
  }, [setMediaIsPlaying]);

  // Listen for digit keypress
  const handleUserKeyPress = useCallback((event: any) => {
    const { key } = event;
    setHideHeader(true);
    setKeyboardTriggerIsActive(true);

    if (key >= '0' && key <= '9') {
      removeAllSubtitles(); 
    }

    for (let i = 0; i <= 9; i++) {
      if (Number(key) === i) {
        captureThumbnail();
        setCurrentIndex(i);
        if (videoRef.current) {
          videoRef.current.currentTime = 0;

        }
      }
    }
  }, [setHideHeader, setKeyboardTriggerIsActive, captureThumbnail, removeAllSubtitles]);

  // Support keypress
  useEffect(() => {
    window.addEventListener('keyup', handleUserKeyPress);
  
    return () => {
      window.removeEventListener('keyup', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  // Display error message if no content is loaded
  if (error) { return <ErrorMessageView title={t("presentationNotFound.title")} body={t("presentationNotFound.body")} redirect={true}></ErrorMessageView> }

  // Only display if published
  if (data?.status && data?.status !== "published") { return <ErrorMessageView title={t("presentationNotPublished.title")} body={t("presentationNotPublished.body")} redirect={true}/> }

  switch(currentFile?.mediaType) {
    case "image":
    case "dm":
      return (
        <>
        <Header title={getTranslation(overrideTitle, currentLanguage) ||(data?.content?.general?.showTitle ? getTranslation(data?.content?.general?.title, currentLanguage) : "")} backgroundColor={data?.content?.background?.backgroundColor}></Header>
        <PageTransition waitFor={data}>
          <Image 
          ref={imageRef}
          src={`${currentFile?.src}?dimension=max`} 
          onLoad={displayImage}
          alt=""
          $objectFit={data?.content?.settings?.display?.objectFit}
          $height={contentHeight}/>
        </PageTransition>
        </>
      );
    case "video":
    case "audio":
      return (
        <>
          <Header title={getTranslation(overrideTitle, currentLanguage) ||(data?.content?.general?.showTitle ? getTranslation(data?.content?.general?.title, currentLanguage) : "")} backgroundColor={data?.content?.background?.backgroundColor}></Header>
          <PageTransition waitFor={data}>
            <PlayContainer ref={playbuttonRef}>
              { /* @ts-ignore */ }
              {keyboardTriggerIsActive && (<Screenshot preserveDrawingBuffer="true" ref={screenshotRef} objectFit={data?.content?.settings?.display?.objectFit} height={contentHeight}/>)}
              <Video autoPlay
              playsInline
              ref={bindVideo}
              src={currentFile?.src} 
              loop={!returnAfterPlay && Object.keys(mediaQueue)?.length === 1}
              onPlaying={onPlaying}
              onProgress={onPlaying}
              onEnded={incrementIndex}
              onClick={playVideo}
              onTimeUpdate={updateProgressBar}
              onCanPlay={displayVideo}
              muted={previewIsActive ? true : false}
              poster={currentFile?.mediaType === "audio" ? currentFile?.thumbnail : undefined}
              $height={contentHeight}
              $objectFit={data?.content?.settings?.display?.objectFit}>
                {closedCaptions && (
                  <track ref={trackRef} default kind="subtitles" src={closedCaptions} />
                )}
              </Video>
              {customSubtitles && subtitleCues.length > 0 && (
                <Subtitles className="kiosk-subtitles">
                  {subtitleStyles && <style>{subtitleStyles}</style>}
                  <span ref={subtitleRef}/>
                </Subtitles>
              )}
              {data?.content?.settings?.display?.showProgressBar === "true" && (
                <ProgressBarWrapper ref={progressWrapperRef} onClick={setProgressPosition} className="clickable"><ProgressBar ref={progressRef} color={data?.content?.settings?.display?.progressBarColor}/></ProgressBarWrapper>
              )}
            </PlayContainer>
          </PageTransition>
        </>
      );

    case "document":
      return (
        <>
          <Header title={getTranslation(overrideTitle, currentLanguage) ||(data?.content?.general?.showTitle ? getTranslation(data?.content?.general?.title, currentLanguage) : "")} backgroundColor={data?.content?.background?.backgroundColor}></Header>
          <PageTransition waitFor={data}>
            <PdfViewer item={currentFile} autoPlay={true} autoPlayDelay={data?.content?.settings?.display?.delay} onAutoPlayEnd={incrementIndex} autoPlayLoop={Object.keys(mediaQueue)?.length === 1}/>
          </PageTransition>
        </>
      );

    case "model3d":
      return (
        <>
          <Header title={getTranslation(overrideTitle, currentLanguage) ||(data?.content?.general?.showTitle ? getTranslation(data?.content?.general?.title, currentLanguage) : "")} backgroundColor={data?.content?.background?.backgroundColor}></Header>
          <PageTransition waitFor={data}>
            <Model3dPopup item={currentFile}/>
          </PageTransition>
        </>
      );

    case "sketchfab":
      return (
        <>
          <Header title={getTranslation(overrideTitle, currentLanguage) ||(data?.content?.general?.showTitle ? getTranslation(data?.content?.general?.title, currentLanguage) : "")} backgroundColor={data?.content?.background?.backgroundColor}></Header>
          <PageTransition waitFor={data}>
            <Iframe width="100%" height="100%" src={`${currentFile?.src}?autostart=1&camera=0&annotations_visible=1&preload=1&annotation_cycle=7&ui_animations=0&ui_infos=0&ui_stop=0&ui_inspector=0&ui_watermark_link=0&ui_watermark=0&ui_ar=0&ui_settings=0&ui_vr=0&ui_fullscreen=0&ui_help=0&ui_vr=0`} sandbox="allow-scripts allow-same-origin"/>
          </PageTransition>
        </>
      );

    default:
      return <Header title={data?.content?.general?.showTitle ? getTranslation(data?.content?.general?.title, currentLanguage) : ""} backgroundColor={data?.content?.background?.backgroundColor}></Header>
  }
}

export default AutoPlayView;